/**
 * 全站唯一 WebSocket 连接（充值到账 / 系统公告等通知）
 *
 * 约定：
 *  - 登录后连接一次，退出登录断开；不要在业务页面各自 new WebSocket
 *  - token 取本地保存的会员 ID，先 Base64 再 URL 编码
 *  - 30 秒心跳，60 秒无 pong 主动重连；退避 1s → 2s → 4s → 8s → 16s → 30s
 */
import { Base64 } from 'js-base64'

const WS_URL = 'wss://api.70x4dzli.com/ws'
const PING_INTERVAL = 30000
const PONG_TIMEOUT = 60000
const RECONNECT_MAX = 30000

let socket = null
let retry = 0
let pingTimer = null
let pongTimer = null
let reconnectTimer = null
let manualClose = false
let connectedToken = null

const handlers = []

/** 订阅全局 WS 消息；返回取消订阅函数 */
export function onMessage(fn) {
  if (typeof fn !== 'function') return () => {}
  handlers.push(fn)
  return () => {
    const index = handlers.indexOf(fn)
    if (index > -1) handlers.splice(index, 1)
  }
}

function emit(msg) {
  handlers.slice().forEach(fn => {
    try {
      fn(msg)
    } catch (e) {
      // 单个订阅异常不能影响其它订阅
    }
  })
}

function stopHeartbeat() {
  if (pingTimer) {
    clearInterval(pingTimer)
    pingTimer = null
  }
  if (pongTimer) {
    clearTimeout(pongTimer)
    pongTimer = null
  }
}

function startHeartbeat() {
  stopHeartbeat()
  pingTimer = setInterval(() => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        socket.send(JSON.stringify({ type: 'ping' }))
      } catch (e) {
        return
      }
      clearTimeout(pongTimer)
      pongTimer = setTimeout(() => {
        if (socket) {
          try { socket.close() } catch (e) {}
        }
      }, PONG_TIMEOUT)
    }
  }, PING_INTERVAL)
}

function scheduleReconnect() {
  if (manualClose) return
  const delay = Math.min(RECONNECT_MAX, 1000 * Math.pow(2, retry))
  retry += 1
  clearTimeout(reconnectTimer)
  reconnectTimer = setTimeout(() => connectWs(), delay)
}

/** 登录后调用；重复调用不会创建多条连接 */
export function connectWs() {
  if (typeof WebSocket === 'undefined') return
  const token = localStorage.getItem('token')
  if (!token) return

  // 换账号时旧连接必须断开
  if (connectedToken && connectedToken !== token) closeWs()
  if (socket && socket.readyState <= 1) return

  // 旧连接正在关闭时先摘掉回调，避免它的 onclose 把新连接置空
  if (socket) {
    socket.onclose = null
    socket.onerror = null
    try { socket.close() } catch (e) {}
    socket = null
  }

  manualClose = false
  connectedToken = token
  clearTimeout(reconnectTimer)
  reconnectTimer = null

  const wsToken = Base64.encode(token)
  const url = WS_URL + '?token=' + encodeURIComponent(wsToken)

  try {
    socket = new WebSocket(url)
  } catch (e) {
    scheduleReconnect()
    return
  }

  socket.onopen = () => {
    retry = 0
    startHeartbeat()
  }

  socket.onmessage = event => {
    let msg
    try {
      msg = JSON.parse(event.data)
    } catch (e) {
      return
    }
    if (!msg) return
    if (msg.type === 'pong') {
      if (pongTimer) {
        clearTimeout(pongTimer)
        pongTimer = null
      }
      return
    }
    emit(msg)
    if (msg.type === 'error') {
      // token 无效：服务端会断开，停止重连，业务侧走重新登录
      closeWs()
    }
  }

  socket.onclose = () => {
    stopHeartbeat()
    socket = null
    connectedToken = null
    if (!manualClose) scheduleReconnect()
  }

  socket.onerror = () => {
    if (socket) {
      try { socket.close() } catch (e) {}
    }
  }
}

/** 退出登录/切账号时调用 */
export function closeWs() {
  manualClose = true
  stopHeartbeat()
  clearTimeout(reconnectTimer)
  reconnectTimer = null
  if (socket) {
    socket.onclose = null
    socket.onerror = null
    try { socket.close() } catch (e) {}
    socket = null
  }
  connectedToken = null
  retry = 0
}

/** 回到前台 / 页面恢复：连接断了就立刻补连 */
function ensureConnection() {
  if (typeof document !== 'undefined' && document.hidden) return
  if (!localStorage.getItem('token')) return
  if (!socket || socket.readyState > 1) connectWs()
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) ensureConnection()
  })
}
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', ensureConnection)
  window.addEventListener('focus', ensureConnection)
}
